import { useState, useEffect } from 'react';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    BarChart3, TrendingUp, Users, Home, Target, ArrowRight,
    DollarSign, Shield, Clock, CheckCircle, Briefcase, Activity, Download
} from 'lucide-react';

export default function AnalyticsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        API.get('/admin/analytics')
            .then(res => setData(res.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, []);

    const downloadPDF = async () => {
        setDownloading(true);
        try {
            const res = await API.get('/reports/analytics-pdf', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `visionaire_analytics_${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) { console.error('PDF download failed', err); }
        finally { setDownloading(false); }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    if (!data) return (
        <div className="min-h-screen bg-[#FAFAFA]">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 pt-24 text-center text-zinc-500">Failed to load analytics</div>
        </div>
    );

    const f = data.funnel;
    const funnelStages = [
        { label: 'Pending', count: f.pending, color: 'bg-amber-500', desc: 'Listings < Day 91' },
        { label: 'Active', count: f.active, color: 'bg-emerald-500', desc: 'Day 91+ on marketplace' },
        { label: 'Matched', count: f.matched, color: 'bg-blue-500', desc: 'Buyer assigned' },
        { label: 'Closed', count: f.closed, color: 'bg-purple-500', desc: 'Deal finalized' },
    ];

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="analytics-page">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']" data-testid="analytics-title">Analytics</h1>
                        <p className="text-sm text-zinc-500 mt-1">Day 91 funnel performance, team metrics, and platform health.</p>
                    </div>
                    <Button onClick={downloadPDF} disabled={downloading} data-testid="download-pdf-btn"
                        className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-sm">
                        <Download className="w-4 h-4 mr-2" />
                        {downloading ? 'Generating...' : 'Download Report'}
                    </Button>
                </div>

                {/* ─── DAY 91 FUNNEL ─── */}
                <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm mb-6" data-testid="funnel-section">
                    <div className="flex items-center gap-2 mb-5">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        <h2 className="text-lg font-medium text-zinc-900 font-['Outfit']">Day 91 Funnel</h2>
                        <Badge className="bg-zinc-100 text-zinc-600 text-[10px] ml-2">{f.total} total listings</Badge>
                    </div>

                    {/* Visual Funnel */}
                    <div className="flex items-end gap-1 h-36 mb-6">
                        {funnelStages.map((s, i) => {
                            const maxCount = Math.max(...funnelStages.map(st => st.count), 1);
                            const height = Math.max(20, (s.count / maxCount) * 100);
                            return (
                                <div key={s.label} className="flex-1 flex flex-col items-center gap-1">
                                    <span className="text-xs font-bold text-zinc-900">{s.count}</span>
                                    <div className={`w-full rounded-t-lg ${s.color} transition-all duration-700`}
                                        style={{ height: `${height}%`, opacity: 0.85 + (i * 0.05) }} />
                                    <span className="text-[10px] text-zinc-500 text-center">{s.label}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Conversion Rates */}
                    <div className="grid grid-cols-3 gap-4">
                        <ConversionCard label="Activation Rate" value={`${f.activation_rate}%`} desc="Pending to Active" icon={ArrowRight} color="emerald" />
                        <ConversionCard label="Match Rate" value={`${f.match_rate}%`} desc="Active to Matched" icon={Target} color="blue" />
                        <ConversionCard label="Close Rate" value={`${f.close_rate}%`} desc="Matched to Closed" icon={CheckCircle} color="purple" />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* ─── USER BREAKDOWN ─── */}
                    <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="user-breakdown">
                        <div className="flex items-center gap-2 mb-4">
                            <Users className="w-5 h-5 text-blue-600" />
                            <h3 className="text-base font-medium text-zinc-900 font-['Outfit']">User Breakdown</h3>
                            <Badge className="bg-zinc-100 text-zinc-600 text-[10px] ml-auto">{data.users.total} total</Badge>
                        </div>
                        <div className="space-y-3">
                            {[
                                { label: 'Buyers', count: data.users.buyers, color: 'bg-emerald-500', icon: Users },
                                { label: 'Sellers', count: data.users.sellers, color: 'bg-amber-500', icon: Home },
                                { label: 'Agents', count: data.users.agents, color: 'bg-blue-500', icon: Briefcase },
                                { label: 'Operators', count: data.users.operators, color: 'bg-purple-500', icon: Target },
                            ].map(u => (
                                <div key={u.label} className="flex items-center gap-3">
                                    <u.icon className="w-4 h-4 text-zinc-400" />
                                    <span className="text-sm text-zinc-700 flex-1">{u.label}</span>
                                    <span className="text-sm font-semibold text-zinc-900">{u.count}</span>
                                    <div className="w-24 h-2 bg-zinc-100 rounded-full overflow-hidden">
                                        <div className={`h-full ${u.color} rounded-full`}
                                            style={{ width: `${Math.max(5, (u.count / Math.max(data.users.total, 1)) * 100)}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ─── CREDIT REPAIR ─── */}
                    <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="credit-repair-stats">
                        <div className="flex items-center gap-2 mb-4">
                            <Shield className="w-5 h-5 text-emerald-600" />
                            <h3 className="text-base font-medium text-zinc-900 font-['Outfit']">Credit Repair</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <StatMini label="Active Clients" value={data.credit_repair.enrolled} color="emerald" />
                            <StatMini label="Total Clients" value={data.credit_repair.total_clients} color="zinc" />
                            <StatMini label="Total Disputes" value={data.credit_repair.total_disputes} color="amber" />
                            <StatMini label="Resolved" value={data.credit_repair.resolved_disputes} color="emerald" />
                        </div>
                        <div className="mt-3 pt-3 border-t border-zinc-100">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-zinc-500">Resolution Rate</span>
                                <span className="font-semibold text-emerald-700">{data.credit_repair.resolution_rate}%</span>
                            </div>
                            <div className="w-full h-2 bg-zinc-100 rounded-full mt-1 overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${data.credit_repair.resolution_rate}%` }} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* ─── OPERATOR LEADERBOARD ─── */}
                    <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="operator-leaderboard">
                        <div className="flex items-center gap-2 mb-4">
                            <Target className="w-5 h-5 text-purple-600" />
                            <h3 className="text-base font-medium text-zinc-900 font-['Outfit']">Operator Performance</h3>
                        </div>
                        {data.operators.length === 0 ? (
                            <div className="text-sm text-zinc-400 py-4 text-center">No operators yet</div>
                        ) : (
                            <div className="space-y-2">
                                {data.operators.sort((a, b) => b.assignments - a.assignments).map((op, i) => (
                                    <div key={i} className="flex items-center gap-3 py-2">
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'}`}>
                                            {i + 1}
                                        </div>
                                        <span className="text-sm text-zinc-700 flex-1">{op.name}</span>
                                        <span className="text-sm font-semibold text-zinc-900">{op.assignments} matches</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ─── AGENT LEADERBOARD ─── */}
                    <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="agent-leaderboard">
                        <div className="flex items-center gap-2 mb-4">
                            <Briefcase className="w-5 h-5 text-blue-600" />
                            <h3 className="text-base font-medium text-zinc-900 font-['Outfit']">Agent Performance</h3>
                        </div>
                        {data.agents.length === 0 ? (
                            <div className="text-sm text-zinc-400 py-4 text-center">No agents yet</div>
                        ) : (
                            <div className="space-y-2">
                                {data.agents.sort((a, b) => b.listings - a.listings).map((ag, i) => (
                                    <div key={i} className="flex items-center gap-3 py-2">
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'}`}>
                                            {i + 1}
                                        </div>
                                        <span className="text-sm text-zinc-700 flex-1">{ag.name}</span>
                                        <span className="text-sm font-semibold text-zinc-900">{ag.listings} listings</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ─── PLATFORM OVERVIEW ─── */}
                <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="platform-overview">
                    <div className="flex items-center gap-2 mb-4">
                        <Activity className="w-5 h-5 text-zinc-600" />
                        <h3 className="text-base font-medium text-zinc-900 font-['Outfit']">Platform Overview</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <OverviewStat label="Buyer Profiles" value={data.overview.total_buyers} icon={Users} />
                        <OverviewStat label="Seller Profiles" value={data.overview.total_sellers} icon={Home} />
                        <OverviewStat label="Active Deals" value={data.overview.total_deals} icon={BarChart3} />
                        <OverviewStat label="Closed Deals" value={data.overview.closed_deals} icon={CheckCircle} />
                        <OverviewStat label="Payments" value={data.overview.total_payments} icon={DollarSign} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function ConversionCard({ label, value, desc, icon: Icon, color }) {
    return (
        <div className={`bg-${color}-50 rounded-xl p-4 border border-${color}-100`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 text-${color}-600`} />
                <span className={`text-xs text-${color}-600`}>{desc}</span>
            </div>
            <div className={`text-2xl font-bold text-${color}-700 font-['Outfit']`}>{value}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">{label}</div>
        </div>
    );
}

function StatMini({ label, value, color }) {
    return (
        <div className="bg-zinc-50 rounded-lg p-3">
            <div className="text-xs text-zinc-500">{label}</div>
            <div className={`text-lg font-semibold text-${color}-700 font-['Outfit']`}>{value}</div>
        </div>
    );
}

function OverviewStat({ label, value, icon: Icon }) {
    return (
        <div className="text-center">
            <Icon className="w-5 h-5 text-zinc-400 mx-auto mb-1" />
            <div className="text-xl font-semibold text-zinc-900 font-['Outfit']">{value}</div>
            <div className="text-[10px] text-zinc-500">{label}</div>
        </div>
    );
}
